import { tokens } from "@blurb/codex-react";
import { GraphQL } from "@rpi/openapi-api";
import { reportUnrecognizedStatus } from "../../../../utils/unrecognizedStatus";
import { getRefundReasonLabel } from "./refundReasonLabels";

export type OrderViewOutletContext = {
  customerPublicId: string;
  customerOrderId: string;
};

export type Order = NonNullable<GraphQL.OrderDetailsQuery["orderByIdentifier"]>;
export type OrderPricing = NonNullable<Order["pricing"]>;
export type OrderRefund = NonNullable<Order["refunds"]>[number];
export type RefundDetails = NonNullable<OrderRefund["refundDetails"]>;

export type RefundBreakdown = {
  currency: GraphQL.SupportedCurrency;
  itemsTotal: number;
  shippingTotal: number;
  taxesTotal: number;
  refundTotal: number;
};

export const flexColumn = { display: "flex", flexDirection: "column" } as const;

export const linkStyle = {
  color: "inherit",
  "font-weight": tokens.font.weight.semibold,
} as const;

function formatDateAndTime(dateString: string, timeZone?: string, timeZoneName?: "short"): string {
  const date = new Date(dateString);
  const datePart = date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric", timeZone });
  const timePart = date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", timeZone, timeZoneName });
  return `${datePart} ${timePart}`;
}

export function formatOrderDate(dateString: string): string {
  return formatDateAndTime(dateString);
}

// UOS records and displays times in Pacific and observes daylight saving, so
// UOS timestamps render in that zone with the zone abbreviation (PST/PDT)
// rather than the viewer's local time.
const UOS_TIME_ZONE = "America/Los_Angeles";

export function formatUosDateTime(dateString: string): string {
  return formatDateAndTime(dateString, UOS_TIME_ZONE, "short");
}

// Whether money was taken at some point, which is what makes the payment's date, method and record worth showing.
// A refund does not erase the payment that preceded it.
export function hasCapturedPayment(status: GraphQL.PaymentStatus | null | undefined): boolean {
  if (!status) return false;
  switch (status) {
    case GraphQL.PaymentStatus.Paid:
    case GraphQL.PaymentStatus.ExternallyPaid:
    case GraphQL.PaymentStatus.ProcessingRefund:
    case GraphQL.PaymentStatus.RefundedFull:
    case GraphQL.PaymentStatus.RefundedShipping:
    case GraphQL.PaymentStatus.RefundedPartial:
      return true;
    case GraphQL.PaymentStatus.Unpaid:
    case GraphQL.PaymentStatus.PaymentPending:
    case GraphQL.PaymentStatus.NoPaymentStaffOrder:
    case GraphQL.PaymentStatus.Voided:
      return false;
    default: {
      // Narrows to never while the cases above are exhaustive, so a new status still fails the build here.
      // At runtime it is whatever the backend sent, which is worth reporting rather than answering silently.
      const unhandled: never = status;
      reportUnrecognizedStatus("PaymentStatus", unhandled);
      return false;
    }
  }
}

export function getCurrencyLabel(currency: GraphQL.SupportedCurrency | null | undefined): string {
  return currency ?? "USD";
}

export function getMostRecentRefund(refunds: OrderRefund[]): OrderRefund | null {
  return refunds.reduce<OrderRefund | null>((latest, r) => {
    if (!latest?.createdAt) return r;
    if (!r.createdAt) return latest;
    return new Date(r.createdAt) > new Date(latest.createdAt) ? r : latest;
  }, null);
}

export function isFullRefund(refund: OrderRefund): boolean {
  return refund.refundDetails?.strategy === GraphQL.BraintreeRefundStrategy.OrderFullValue;
}

export function getRefundKindLabel(refund: OrderRefund): string {
  if (!refund.refundDetails) return "Refund";
  return isFullRefund(refund) ? "Full Refund" : "Partial Refund";
}

export function getRefundReasonDisplay(refund: OrderRefund): string | null {
  const reasonLabel = getRefundReasonLabel(refund.refundDetails?.reason);
  const note = refund.refundDetails?.reasonNote?.trim();
  if (reasonLabel && note) return `${reasonLabel} (${note})`;
  return reasonLabel ?? note ?? null;
}

// Returns null for legacy refunds created before the refund_details column existed.
export function toRefundBreakdown(
  refund: OrderRefund,
  currency: GraphQL.SupportedCurrency | null | undefined
): RefundBreakdown | null {
  const details = refund.refundDetails;
  if (!details) return null;
  return {
    currency: currency ?? GraphQL.SupportedCurrency.Usd,
    itemsTotal: details.itemsRefundAmount,
    shippingTotal: details.shippingRefundAmount,
    taxesTotal: details.taxesRefundAmount,
    refundTotal: refund.amount ?? 0,
  };
}
